import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import {Box, Button, Grid, Tooltip, Typography} from '@mui/material';
import {get_percent_str, get_price_str, get_value, is_mobile_device} from 'common';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IValueSet} from '../types';

function ControlButton(props: {index: number; value: IValueSet; onChange: any; negative?: boolean; unit: number}) {
	const {index, value, negative, unit, onChange} = props;

	const is_mobile = is_mobile_device();

	const mobile_icons_left = [KeyboardDoubleArrowLeftIcon, KeyboardArrowLeftIcon];
	const mobile_icons_right = [KeyboardArrowRightIcon, KeyboardDoubleArrowRightIcon];

	const mobile_icons = useMemo(() => (unit > 0 ? mobile_icons_right : mobile_icons_left), [unit]);
	const unit_str = useMemo(() => get_percent_str(unit), [unit]);

	const color = unit > 0 ? 'secondary' : 'error';

	const handleModifierChange = useCallback(() => {
		const new_value = {...value, modifier: !negative && value.modifier + unit / 100 <= 0 ? 0 : value.modifier + unit / 100};
		onChange(new_value);
	}, [value, unit, negative, onChange]);

	return (
		<Button
			key={unit}
			id={`minus-${unit}`}
			fullWidth
			variant={'outlined'}
			size="small"
			onClick={handleModifierChange}
			color={color}
			sx={{
				fontSize: '0.65rem',
				textTransform: 'none',
				minWidth: 0,
				px: 0.5,
				py: 0.25,
			}}
		>
			{is_mobile ? <Box component={mobile_icons[index]} /> : unit_str}
		</Button>
	);
}

export default function ValueSetter(props: {
	name: string;
	value: IValueSet;
	units?: number[];
	negative?: boolean;
	color?: string;
	bgcolor?: string;
	tooltip?: string;
	onChange: (amount: IValueSet) => void;
}) {
	const {name, units, value, negative, onChange, color, bgcolor, tooltip} = props;

	const plus_units = useMemo(() => units ?? [0.05, 0.1], [units]);
	const minus_units = useMemo(() => [...plus_units].reverse().map(unit => -unit), [plus_units]);

	const initial_value_ref = useRef<IValueSet>();
	const [amount_str, set_amount_str] = useState('↺');
	const [percent_str, set_percent_str] = useState('↺');

	const handleReset = useCallback(() => {
		if (initial_value_ref.current && value) {
			onChange({
				modifier: initial_value_ref.current.modifier,
				ref_value: value.ref_value,
			});
		}
	}, [value, onChange]);

	useEffect(() => {
		if (value) {
			if (!initial_value_ref.current) initial_value_ref.current = value;

			set_percent_str(get_percent_str(value.modifier * 100, !negative, 2));
			set_amount_str(get_price_str(get_value(value), !negative));
		}
	}, [value]);

	return (
		<Box width="100%" display="flex" justifyContent="space-between" gap="10px">
			<Box id="down-controller" display="flex" gap="5px">
				{minus_units.map((unit, index) => (
					<ControlButton key={unit} index={index} value={value} negative={negative} unit={unit} onChange={onChange} />
				))}
			</Box>

			<Box id="numeric-box" display="flex" alignItems="center">
				<Box
					id="ratio"
					padding="5px"
					minWidth="80px"
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					borderRadius="5px 0 0 5px"
					bgcolor={'grey.900'}
					onClick={handleReset}
					sx={{cursor: 'pointer'}}
				>
					<Typography variant="caption" color="white" marginRight="1px">
						{name}
					</Typography>

					<Typography variant="body2" color={color ?? 'text.primary'}>
						{percent_str}
					</Typography>
				</Box>

				<Tooltip title={tooltip} placement="top" arrow>
					<Box
						id="amount"
						width="90px"
						padding="5px"
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						borderRadius="0px 5px 5px 0px"
						bgcolor={bgcolor ?? 'grey.800'}
					>
						<Typography variant="caption" color="text.disabled">
							{amount_str}
						</Typography>
					</Box>
				</Tooltip>
			</Box>

			<Box id="up-controller" display="flex" gap="5px">
				{plus_units.map((unit, index) => (
					<ControlButton key={unit} index={index} value={value} negative={negative} unit={unit} onChange={onChange} />
				))}
			</Box>
		</Box>
	);
}
